import React, { useState } from 'react';
import {
    Box,
    MenuItem,
    Skeleton,
    FormControl,
    InputLabel,
    Select,
    Tab,
    GlobalStyles,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Chart from 'react-apexcharts';
import useGatewayChart from './gateway-chart.hooks';
import { Styled } from './gateway-chart.styled';
import PersianDatePickerRange from 'components/formik/date-picker/date-picker-range';

const GatewayChart = ({
    staticChart,
    fixedTimeline,
    height = 200,
    disabled = false,
}: {
    staticChart?: Record<string, number | string> | undefined;
    fixedTimeline?: ChartPeriod;
    height?: number;
    disabled?: boolean;
}) => {
    const theme = useTheme();
    const [chartType, setChartType] = useState<ChartType>('income');

    const {
        data,
        timeline,
        series,
        options,
        isLoading,
        datepickerOpen,
        setDatepickerOpen,
        onDatePickerChange,
        onChange,
    } = useGatewayChart({ chartType, staticChart, fixedTimeline, disabled });

    const handleChartTypeChange = (
        event: React.SyntheticEvent,
        newValue: number,
    ) => {
        const selectedType = Object.keys(chartTypeLabels)[
            newValue
        ] as ChartType;
        setChartType(selectedType);
    };

    return (
        <>
            <GlobalStyles
                styles={{
                    ':root': {
                        '--apex-tooltip-bg': theme.palette.background.paper,
                        '--apex-tooltip-title-bg':
                            theme.palette.background.light,
                        '--apex-tooltip-text': theme.palette.text.primary,
                        '--apex-tooltip-label': theme.palette.text.secondary,
                        '--apex-tooltip-border': theme.palette.divider,
                    },
                }}
            />

            {!staticChart && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between',
                        px: { xs: 0.25, sm: 1 },
                        mt: 0.35,
                        mb: -1.2,
                        gap: 1.1,
                    }}>
                    {/* Chart Type Tabs */}
                    <Styled.TabsContainer>
                        <Styled.Tabs
                            value={Object.keys(chartTypeLabels).indexOf(
                                chartType,
                            )}
                            onChange={handleChartTypeChange}
                            orientation="horizontal"
                            variant="scrollable"
                            scrollButtons={false}
                            allowScrollButtonsMobile>
                            {Object.entries(chartTypeLabels).map(
                                ([key, label]) => (
                                    <Tab
                                        key={key}
                                        label={label}
                                        disabled={isLoading || disabled}
                                        sx={{ fontSize: 11.75 }}
                                    />
                                ),
                            )}
                        </Styled.Tabs>
                    </Styled.TabsContainer>
                    {/* Timeline Selector */}
                    <Styled.TabsContainer>
                        <FormControl fullWidth size="small">
                            <InputLabel id="timeline-label">
                                بازه زمانی
                            </InputLabel>
                            <Select
                                labelId="timeline-label"
                                id="timeline"
                                name="timeline"
                                value={timeline}
                                onChange={onChange}
                                label="بازه زمانی"
                                disabled={isLoading || disabled}
                                renderValue={(selected) => {
                                    const selectedItem = data?.find(
                                        (item) => item.name === selected,
                                    );

                                    return selectedItem?.label;
                                }}
                                sx={(theme) => ({
                                    minWidth: '138px',
                                    borderRadius: '11px',
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,.025)'
                                            : 'rgba(17,24,39,.018)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'divider',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.35),
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                    '& .MuiSelect-select': {
                                        fontSize: 11.75,
                                        py: 1.05,
                                    },
                                })}>
                                {data?.map(
                                    (row: ChartDataItem, index: number) => (
                                        <MenuItem
                                            key={index}
                                            value={row.name}
                                            onClick={() =>
                                                row.name === 'custom'
                                                    ? setDatepickerOpen(true)
                                                    : undefined
                                            }
                                            disabled={isLoading || disabled}
                                            sx={{ fontSize: 11.75 }}>
                                            {index === data?.length - 1
                                                ? 'انتخاب بازه دلخواه'
                                                : row.label}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                    </Styled.TabsContainer>
                </Box>
            )}

            {/* Custom Date Picker */}
            <PersianDatePickerRange
                noTextField={{
                    isOpenCalender: !!datepickerOpen,
                    setDatepickerOpen,
                    onChange: onDatePickerChange,
                }}
            />

            {/* Chart Display */}
            <>
                <Chart
                    options={options}
                    series={series}
                    type="area"
                    height={`${height}px`}
                    width="100%"
                    style={{
                        display: !isLoading ? 'block' : 'none',
                        maxWidth: '100%',
                    }}
                />

                <Box
                    sx={{
                        px: 1.5,
                        mt: 2,
                        display: !!isLoading ? 'block' : 'none',
                    }}>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={height}
                    />
                </Box>
            </>
        </>
    );
};

export default GatewayChart;

type ChartType = 'income' | 'transaction';

export interface ChartDataItem {
    name: ChartPeriod;
    label: string;
    categories?: string[];
    series?: ChartSeries[];
}

interface ChartSeries {
    name: string;
    data: number[];
}

export type ChartPeriod =
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'threeMonths'
    | 'sixMonths'
    | 'yearly'
    | 'custom';

const chartTypeLabels: Record<ChartType, string> = {
    income: 'مبلغ درآمد',
    transaction: 'تعداد تراکنش',
};
